import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'wood' | 'sky' | 'outline'
type Size = 'sm' | 'md' | 'lg'

interface BaseProps {
  children: ReactNode
  variant?: Variant
  size?: Size
  /** Variant B blocky-beveled look (pixel buttons). */
  blocky?: boolean
  className?: string
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-grass-dark hover:bg-grass-deep text-white border-grass-deep',
  secondary:
    'bg-gold hover:bg-gold-dark text-slate-deep border-gold-dark',
  wood: 'bg-wood hover:bg-wood-dark text-white border-wood-dark',
  sky: 'bg-sky-deep hover:bg-sky text-white border-sky-deep',
  outline:
    'bg-white hover:bg-grass/10 text-grass-deep border-grass-dark',
}

const sizeClasses: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
}

function classes({ variant = 'primary', size = 'md', blocky = false, className = '' }: BaseProps) {
  const shape = blocky
    ? 'font-pixel tracking-tight border-b-4 active:border-b-2 active:translate-y-[2px] rounded-none uppercase'
    : 'font-extrabold rounded-xl border-b-4 active:border-b-2 active:translate-y-[2px]'
  return [
    'inline-flex items-center justify-center gap-2 transition-all select-none cursor-pointer shadow-block',
    blocky ? 'text-xs leading-relaxed' : '',
    variantClasses[variant],
    sizeClasses[size],
    shape,
    className,
  ]
    .filter(Boolean)
    .join(' ')
}

interface LinkButtonProps extends BaseProps {
  to: string
}

export function LinkButton({ to, ...rest }: LinkButtonProps) {
  return (
    <Link to={to} className={classes(rest)}>
      {rest.children}
    </Link>
  )
}

interface AnchorButtonProps extends BaseProps {
  href: string
  target?: string
  rel?: string
}

export function AnchorButton({ href, target, rel, ...rest }: AnchorButtonProps) {
  return (
    <a href={href} target={target} rel={rel} className={classes(rest)}>
      {rest.children}
    </a>
  )
}

interface ButtonProps extends BaseProps {
  onClick?: () => void
  type?: 'button' | 'submit'
  disabled?: boolean
}

export default function Button({ onClick, type = 'button', disabled, ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes(rest) + (disabled ? ' opacity-50 cursor-not-allowed' : '')}
    >
      {rest.children}
    </button>
  )
}
